import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { CustomColorPicker } from "../CustomColorPicker";
import { Plus, Trash2 } from "lucide-react";

export function ColorPickerDialog({ picker, colorFor, onApply, onClose }) {
  return (
    <Dialog open={!!picker} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-sm" data-testid="color-picker-dialog">
        <DialogHeader>
          <DialogTitle>Choose a colour</DialogTitle>
          <DialogDescription>{picker?.title ? `Applying to ${picker.title}` : "Pick any colour, enter a hex code, or use the eyedropper."}</DialogDescription>
        </DialogHeader>
        {picker && <CustomColorPicker initial={colorFor(picker.ids[0]) || "#082D4A"} onCancel={onClose} onApply={(c) => { onApply(picker.ids, c); onClose(); }} />}
      </DialogContent>
    </Dialog>
  );
}

export function ThemesDialog({ open, onOpenChange, themes, onSave, onDelete }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="themes-dialog">
        <DialogHeader>
          <DialogTitle>Colour themes</DialogTitle>
          <DialogDescription>Save your current tree colours as a reusable palette, then apply a theme to any branch from a card's right-click menu.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Button onClick={onSave} data-testid="save-palette-button" className="w-full bg-[#082D4A] hover:bg-[#08223A]"><Plus size={16} className="mr-2" /> Save current tree colours as a theme</Button>
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {themes.length === 0 && <p className="text-sm text-[#64748B]">No themes yet. Colour some people, then save the palette here.</p>}
            {themes.map((th) => (
              <div key={th.id} data-testid={`theme-row-${th.id}`} className="flex items-center gap-3 rounded-lg border border-[#D8E0E8] p-2.5">
                <div className="flex flex-1 flex-col gap-1.5">
                  <span className="text-sm font-semibold text-[#082D4A]">{th.name}</span>
                  <div className="flex flex-wrap gap-1">{(th.colors || []).map((c, i) => (<span key={`${th.id}-${i}`} className="h-5 w-5 rounded-full ring-1 ring-black/10" style={{ backgroundColor: c }} />))}</div>
                </div>
                <button onClick={() => onDelete(th)} data-testid={`delete-theme-${th.id}`} title="Delete theme" className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#64748B]">Tip: right-click any person → Change colour → "Apply saved theme to branch" to colour their whole branch by sub-family.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, hint, value, onChange, placeholder, testId }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold text-[#082D4A]">{label} {hint && <span className="font-normal text-[#64748B]">{hint}</span>}</label>
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} />
  </div>
);

export function LegendExportDialog({ open, onClose, title, setTitle, date, setDate, note, setNote, onExport }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="legend-title-dialog">
        <DialogHeader>
          <DialogTitle>Add a title to your legend export</DialogTitle>
          <DialogDescription>These appear above the colour legend on the exported image, for a keepsake look. Leave blank to skip.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Family title" value={title} onChange={setTitle} placeholder="The Walters Family" testId="legend-title-input" />
          <Field label="Date" value={date} onChange={setDate} placeholder="June 2026" testId="legend-date-input" />
          <Field label="Caption / dedication" hint="(optional)" value={note} onChange={setNote} placeholder="In loving memory — the story of our roots" testId="legend-note-input" />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose} data-testid="legend-cancel">Cancel</Button>
            <Button onClick={onExport} data-testid="legend-export-confirm" className="bg-[#082D4A] hover:bg-[#08223A]">Export</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
